<template>
  <div class="shipment-detail-status-map tw-relative">
    <div
      ref="container"
      class="shipment-detail-status-map__map"
      data-test="map"
    />

    <div
      v-if="hasDistance"
      class="tw-flex shipment-detail-status-map__distance__container tw-w-full"
    >
      <div class="shipment-detail-status-map__distance tw-rounded">
        {{ $t('shipment.labels.distance') }} <span class="tw-font-medium">{{ $n(distance) }}&nbsp;km</span>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { defineComponent } from '@vue/composition-api'

  import {
    BASE_MAP_OPTIONS,
    addZoomControl,
    bindPopup,
    createTooltip,
    fitToPositions,
    icons,
    loadGoogleMaps,
    popupAnchorPoint
  } from '@/services/GoogleMaps'
  import { Shipment } from '@/resources'
  import { EventBus } from '@/services/EventBus'

  /**
   * @module component - shipmentDetailStatusMap
   */
  export default defineComponent({
    name: 'ShipmentDetailStatusMap',
    computed: {
      ...mapGetters('auth', [
        'getCid'
      ]),
      ...mapGetters('shipments', [
        'getCurrentShipment'
      ]),
      /**
       * Returns true if there is a mission
       * @function hasMission
       */
      hasMission () {
        return !!this.getCurrentShipment.mission
      },
      /**
       * Returns the padding kept around whatever the camera frames.
       * @function flyPadding
       */
      flyPadding () {
        return { top: 64, right: 64, bottom: 64, left: 64 }
      },
      /**
       * Returns the distance in km
       * @function distance
       * @returns {number|string}
       */
      distance () {
        return (this.getCurrentShipment.travel.distance / 1000).toFixed(0)
      },
      /**
       * @function location
       * @returns {object}
       */
      location () {
        return (/** @type {string} */ direction) => this.hasMission
          ? this.getCurrentShipment.mission[direction].address.location
          : this.getCurrentShipment[direction].address.location
      },
      /**
       * @function isCancelled
       * @returns {boolean}
       */
      isCancelled () {
        const cancellationStates = ['cancelled', 'expired']
        return cancellationStates.includes(this.getCurrentShipment.state)
      },
      /**
       * Returns the appropriate marker icon according to the shipment & the pickup state.
       * @function getPickupIcon
       * @returns {any} icon
       */
      getPickupIcon () {
        const shipment = this.getCurrentShipment
        if (this.isCancelled) return icons.pickup('cancelled')
        if (shipment.state === 'planned') return icons.pickup('scheduled')

        return shipment.pickup.state === 'completed'
          ? icons.pickup('completed')
          : icons.pickup('in_progress')
      },
      /**
       * Returns the appropriate marker icon according to the shipment & the delivery state.
       * @function getDeliveryIcon
       * @returns {any} icon
       */
      getDeliveryIcon () {
        const shipment = this.getCurrentShipment
        if (this.isCancelled) return icons.delivery('cancelled')

        return shipment.delivery.state === 'completed'
          ? icons.delivery('completed')
          : icons.delivery('in_progress')
      }
    },
    data () {
      return {
        itineraryVisible: false,
        itinerary: null,
        hasDistance: false,
        canFlyToItinerary: false,
        /**
         * Everything drawn on the map, kept so it can be taken off again. The
         * map itself is not reactive: Vue has no business walking a Google map.
         * @type {any}
         */
        overlays: {
          markers: {},
          tooltips: [],
          route: null
        },
        itineraryOptions: {
          style: {
            color: '#287696',
            opacity: 1,
            weight: 3
          }
        },
        mapData: {
          zoom: 4,
          maxZoom: 14,
          minZoom: 2,
          center: { lat: 48.8664982, lng: 2.3348235 }
        }
      }
    },
    async mounted () {
      EventBus.$on('shipment:loaded', this.fetchItinerary)

      await this.createMap()
    },
    methods: {
      /**
       * Builds the map and drops both markers, each with the popup its address
       * opens. Held as a promise so `fetchItinerary` can wait for it however
       * early the shipment lands.
       * @function createMap
       * @returns {Promise<any>} map
       */
      createMap () {
        this.mapReady = loadGoogleMaps().then(maps => {
          if (!this.$refs.container) return null

          this.map = new maps.Map(this.$refs.container, {
            ...BASE_MAP_OPTIONS,
            zoom: this.mapData.zoom,
            maxZoom: this.mapData.maxZoom,
            minZoom: this.mapData.minZoom,
            center: this.mapData.center,
            scrollwheel: false
          })

          addZoomControl(this.map)
          this.drawMarkers()

          return this.map
        })

        return this.mapReady
      },
      /**
       * @function drawMarkers
       */
      drawMarkers () {
        if (!this.map || !this.getCurrentShipment) return

        const directions = ['pickup', 'delivery']
        directions.forEach(direction => {
          const marker = new window.google.maps.Marker({
            map: this.map,
            position: this.location(direction),
            icon: direction === 'pickup' ? this.getPickupIcon : this.getDeliveryIcon,
            anchorPoint: popupAnchorPoint()
          })

          bindPopup(this.map, marker, this.formatPopup(direction))

          this.overlays.markers[direction] = marker
        })
      },
      async fetchItinerary () {
        const shipment = this.getCurrentShipment
        if (shipment) {
          const map = await this.mapReady
          if (!map) return

          /**
           * The markers are dropped with the map, but a shipment landing after
           * a state change brings new icons and a new popup with it.
           */
          this.redrawMarkers()

          await this.retrieveItinerary()

          /**
           * According to the state of the shipment, either zoom to the markers
           * or zoom to a specific marker.
           */
          const pendingStates = ['pending', 'late']
          const inProgressStates = ['started', 'transit', 'near_delivery', 'delivered']
          const finishedStates = ['finished', 'proof_of_delivery_available']

          const { pickup, delivery, state } = shipment
          const isPlanned = state === 'planned'
          const isFinished = finishedStates.includes(state)

          const isPickupPending = inProgressStates.includes(state) &&
            pendingStates.includes(pickup.state)
          const isDeliveryPending = inProgressStates.includes(state) &&
            pendingStates.includes(delivery.state)
          const isPickupCompleted = pickup.state === 'completed' ||
            isFinished
          const isDeliveryCompleted = shipment.delivery.state === 'completed' ||
            isFinished

          this.canFlyToItinerary = false
          if (isPlanned || isPickupPending) {
            this.flyToMarker('pickup')
            if (isPickupCompleted) {
              this.addCompletedTooltip('pickup')
            } else if (isPlanned) {
              this.addPlannedTooltip()
              this.hasDistance = true
            } else {
              this.addInProgressTooltip('pickup')
              this.hasDistance = true
            }
          } else if (!isPickupPending && isDeliveryPending) {
            this.flyToMarker('delivery')
            if (isDeliveryCompleted) this.addCompletedTooltip('delivery')
            else {
              this.addInProgressTooltip('delivery')
              this.hasDistance = true
            }
          } else {
            this.canFlyToItinerary = true
            this.flyToMarkers()
            if (isPickupCompleted && isDeliveryCompleted) {
              this.addCompletedTooltip('pickup')
              this.addCompletedTooltip('delivery')
            }
          }

          /**
           * Once the zooming animations are over, show the map itinerary
           */
          let zoomed = false
          window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
            if (zoomed) return
            zoomed = true
            this.itineraryVisible = true
            this.showItinerary()

            this.$nextTick(() => {
              if (this.canFlyToItinerary) this.flyToItinerary()
            })
          })
        }
      },
      /**
       * @function redrawMarkers
       */
      redrawMarkers () {
        Object.values(this.overlays.markers).forEach(marker => marker.setMap(null))
        this.overlays.markers = {}
        this.drawMarkers()
      },
      /**
       * @function addTooltip
       * @param {string} direction
       * @param {string} content
       * @param {object} options
       */
      async addTooltip (direction, content, options) {
        await this.$nextTick()

        const marker = this.overlays.markers[direction]
        if (!marker || !this.map) return

        const position = this.location(direction)
        this.overlays.tooltips.push(createTooltip(this.map, {
          position,
          content,
          className: options.className || `tooltip-${direction}`,
          direction: options.direction,
          offset: options.offset
        }))
      },
      /**
       * Adds a tooltip for the marker, with the completed textual content
       * @function addCompletedTooltip
       * @param {string} direction
       * @returns {Promise<any>}
       */
      async addCompletedTooltip (direction) {
        await this.$nextTick()

        /**
         * According to the pickup & delivery positions in the screen, suppose the
         * bearing of the pickup->delivery itinerary. World coordinates are enough
         * for that: they grow eastwards exactly as the screen does, whatever the
         * current zoom.
         */
        if (!this.map) return false

        const bearing = this.location('delivery').lng - this.location('pickup').lng

        const content = `${this.$t(direction === 'pickup'
          ? 'shipment.labels.pickup_done'
          : 'shipment.labels.delivery_done')}`

        /**
         * Depending of the direction & bearing, set the direction & the offset accordingly.
         */
        this.addTooltip(direction, content, {
          direction: direction === 'pickup'
            ? (bearing > 0
              ? 'left'
              : 'right')
            : (bearing > 0
              ? 'right'
              : 'left'),
          offset: direction === 'pickup'
            ? (bearing > 0
              ? [-10, 0]
              : [10, 0])
            : (bearing > 0
              ? [10, 0]
              : [-10, 0])
        })
      },
      /**
       * Adds a tooltip for the marker, with the in progress textual content
       * @function addInProgressTooltip
       * @param {string} direction
       */
      addInProgressTooltip (direction) {
        const dir = this.hasMission
          ? this.getCurrentShipment.mission[direction]
          : this.getCurrentShipment[direction]

        const { date: handlingDate } = dir.time_slot

        const date = handlingDate
          ? `<br /><span class="tw-font-medium tw-text-white">${this.$moment(handlingDate).format('LL')}</span>`
          : ''

        const content = `${this.$t(direction === 'pickup' ? 'shipment.labels.pickup_planned_at' : 'shipment.labels.delivery_planned_at')} ${date}`

        /**
         * On a mobile viewport, we want to change the direction of the tooltip, to avoid
         * being cropped.
         */
        const isMobile = Math.max(document.documentElement.clientWidth, window.innerWidth || 0) <= 455
        const tooltipDirection = isMobile ? 'top' : 'left'

        this.addTooltip(direction, content, {
          className: `tooltip-${direction} ${direction === 'pickup' ? 'tw-bg-blue-500' : 'tw-bg-green-500'}`,
          direction: tooltipDirection,
          offset: isMobile ? [0, -40] : [-10, 0]
        })
      },
      /**
       * Adds a tooltip for the marker, with the planned textual content
       * @function addPlannedTooltip
       */
      addPlannedTooltip () {
        const shipment = this.getCurrentShipment
        const dir = this.hasMission
          ? shipment.mission.pickup
          : shipment.pickup

        const handlingDate = dir.time_slot.date
        const date = handlingDate
          ? `<br /><span class="tw-font-medium tw-text-white">${this.$moment(handlingDate).fromNow(true)}</span>`
          : ''

        const content = `${this.$t('shipment.labels.planned')} ${date}`

        this.addTooltip('pickup', content, {
          className: 'tooltip-pickup tw-bg-blue-500',
          direction: 'left',
          offset: [-10, 0]
        })
      },
      /**
       * @function formatPopup
       * @param {string} direction
       */
      formatPopup (direction) {
        const dir = this.hasMission
          ? this.getCurrentShipment.mission[direction]
          : this.getCurrentShipment[direction]

        const { address } = dir

        const comment = `
          <div class="content tw-text-gray-700">
            ${address.comment}
          </div>
        `
        return `
          <div class="header ${direction}">
            <b>${address.name}</b><br>
            ${address.street_name}<br>
            ${address.postal_code} ${address.city}, ${this.$t(address.country)}
          </div>
          ${address.comment ? comment : ''}
        `
      },
      /**
       * @function flyToMarker
       * @param {string} direction
       */
      flyToMarker (direction) {
        this.readyToFly((map) => {
          const marker = this.overlays.markers[direction]
          if (!marker) return

          this.hideItinerary()
          map.panTo(marker.getPosition())
          map.setZoom(14)
        })
      },
      /**
       * Make the camera fly to the boundaries of the markers
       * @function flyToMarkers
       */
      flyToMarkers () {
        this.readyToFly((map) => {
          const markers = Object.values(this.overlays.markers)
          if (!markers.length) return

          this.hideItinerary()
          fitToPositions(map, markers.map(marker => marker.getPosition()), {
            padding: this.flyPadding
          })
        })
      },
      /**
       * Make the camera fly to the boundaries of the itinerary
       * @function flyToItinerary
       */
      flyToItinerary () {
        this.readyToFly((map) => {
          if (!this.itinerary) return

          fitToPositions(map, this.itinerary, { padding: this.flyPadding })
        })
      },
      /**
       * Draws the itinerary, or redraws it once its points have changed.
       * @function showItinerary
       */
      showItinerary () {
        if (this.overlays.route) {
          this.overlays.route.setMap(null)
          this.overlays.route = null
        }

        if (!this.map || !this.itinerary || !this.itineraryVisible) return

        this.overlays.route = new window.google.maps.Polyline({
          map: this.map,
          path: this.itinerary,
          strokeColor: this.itineraryOptions.style.color,
          strokeOpacity: this.itineraryOptions.style.opacity,
          strokeWeight: this.itineraryOptions.style.weight
        })
      },
      /**
       * @function hideItinerary
       */
      hideItinerary () {
        this.itineraryVisible = false
        if (this.overlays.route) this.overlays.route.setVisible(false)
      },
      /**
       * Calls the callback once we're ready to make the flyTo animation
       * @function readyToFly
       * @param {function} callback
       */
      readyToFly (callback) {
        setTimeout(() => {
          if (!this.map) return

          /**
           * The map is often built while its panel is still being laid out, and
           * Google sizes itself once: without this it draws into the size the
           * container had then.
           */
          window.google.maps.event.trigger(this.map, 'resize')
          this.$nextTick(() => {
            callback(this.map)
          })
        }, 300)
      },
      /**
       * @function retrieveItinerary
       * @return {Promise<any>}
       */
      retrieveItinerary () {
        return Shipment.travel({
          cid: this.getCid,
          sid: this.getCurrentShipment.uuid
        })
          .then(res => {
            this.itinerary = res.data.itinerary.coordinates.map((/** @type {[number, number]} */ location) => ({
              lat: location[1],
              lng: location[0]
            }))

            this.showItinerary()

            if (this.canFlyToItinerary) this.flyToItinerary()
          })
          .catch(() => {})
      }
    },
    beforeDestroy () {
      EventBus.$off('shipment:loaded', this.fetchItinerary)

      Object.values(this.overlays.markers).forEach(marker => marker.setMap(null))
      this.overlays.tooltips.forEach(tooltip => tooltip.setMap(null))
      if (this.overlays.route) this.overlays.route.setMap(null)
    }
  })
</script>

<style lang="scss">
  .shipment-detail-status-map {
    &__map {
      width: 100%;
      height: 280px;
      z-index: 0;
    }

    &__distance {
      background-color: rgba(white, 0.8);
      padding: 4px 16px;
      z-index: 500;
      text-align: center;
      box-shadow: 0 3px 5px rgba(black, 0.12);
      margin: auto;

      &__container {
        position: absolute;
        bottom: 16px;
      }
    }

    .ctk-map-tooltip {
      font-size: 14px;
      font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
      padding: 8px 16px;

      &.tooltip-pickup.tw-bg-blue-500 {
        border: none;
        color: white;

        &.ctk-map-tooltip--top::before {
          border-top-color: $info;
        }

        &.ctk-map-tooltip--left::before {
          border-left-color: $info;
        }
      }

      &.tooltip-delivery.tw-bg-green-500 {
        border: none;
        color: white;

        &.ctk-map-tooltip--top::before {
          border-top-color: $primary;
        }

        &.ctk-map-tooltip--left::before {
          border-left-color: $primary;
        }
      }
    }
  }
</style>
